// ============================================================================
// apps/web/src/lib/mm-task-client.ts
// ----------------------------------------------------------------------------
// Client pour le backend MM Distribution (todo list MM) — projet Supabase
// SÉPARÉ de Magic, qui héberge :
//   - public.taches              (tâches)
//   - public.collaborateurs      (assignees)
//   - public.commentaires        (commentaires sur tâches)
//   - public.app_settings        (catégories + magasins)
//   - bucket storage 'taches-fichiers'
//
// Utilise l'anon key (publique, exposée volontairement côté browser dans le
// formulaire MM officiel à https://task.lepitchoun-traiteur.fr). Pas de
// service_role, pas d'écriture sensible — RLS de MM Distribution s'en charge.
// ============================================================================

const MM_URL = 'https://lywbunctnsjzpcgkzqox.supabase.co';
const MM_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imx5d2J1bmN0bnNqenBjZ2t6cW94Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQ0NTc3OTUsImV4cCI6MjA5MDAzMzc5NX0.3RdC94mX9x8PZ4itEEUPwmRtMryB4xd_vQY6y4h05ms';

function mmHeaders(extra: Record<string, string> = {}): Record<string, string> {
  return {
    apikey: MM_KEY,
    Authorization: `Bearer ${MM_KEY}`,
    ...extra,
  };
}

export type MMCollab = {
  id: number;
  name: string;
  initials: string;
};

export type MMTaskFile = {
  name: string;
  path: string;
  type: 'pdf' | 'image';
  url: string;
};

export type MMTaskPriority = 'high' | 'med' | 'low';

export type CreateMMTaskInput = {
  title: string;
  category?: string;       // ex: 'Matériel à acheter'
  subtype?: string;        // tag secondaire (ex: 'maintenance')
  magasin: string;         // ex: 'Magasin principal'
  priority: MMTaskPriority;
  assigneeIds?: number[];
  files?: File[];
  comment?: string | null;
  commentAuthor?: string | null;
};

export type CreateMMTaskResult = {
  taskId: number;
  filesUploaded: number;
  filesFailed: number;
};

/**
 * Liste des magasins déclarés côté MM Distribution. Stockés dans
 * `app_settings.key = 'magasins'` sous forme de array JSON.
 */
export async function fetchMMStores(): Promise<string[]> {
  const r = await fetch(
    `${MM_URL}/rest/v1/app_settings?key=eq.magasins`,
    { headers: mmHeaders() },
  );
  if (!r.ok) return [];
  const rows = await r.json();
  const row = rows?.[0];
  return Array.isArray(row?.value) ? row.value : [];
}

/**
 * Catégories de tâche déclarées côté MM Distribution. Stockés dans
 * `app_settings.key = 'categories'`. Fallback sur le set par défaut si vide.
 */
export async function fetchMMCategories(): Promise<string[]> {
  const r = await fetch(
    `${MM_URL}/rest/v1/app_settings?key=eq.categories`,
    { headers: mmHeaders() },
  );
  if (!r.ok) return DEFAULT_MM_CATEGORIES;
  const rows = await r.json();
  const row = rows?.[0];
  if (Array.isArray(row?.value) && row.value.length > 0) return row.value;
  return DEFAULT_MM_CATEGORIES;
}

const DEFAULT_MM_CATEGORIES = [
  'Matériel à acheter',
  'Divers opérationnel',
  'Dev co event',
  'Communication',
  'Gestion admin',
];

export type MMInterventionRequest = {
  id: number;
  title: string;
  magasin: string | null;
  priority: MMTaskPriority | null;
  status: string;
  created_at: string | null;
};

/**
 * Demandes d'intervention encore ouvertes côté MM (celles envoyées depuis
 * Magic via « Demande d'intervention » du 4-en-1 : category = 'Demande
 * intervention'). On exclut celles marquées terminées (`status = 'done'`) →
 * une demande traitée dans MM disparaît d'elle-même du tableau de supervision.
 * Best-effort : projet MM séparé, renvoie [] si indisponible.
 */
export async function fetchMMInterventionRequests(): Promise<MMInterventionRequest[]> {
  const url =
    `${MM_URL}/rest/v1/taches` +
    `?select=id,title,magasin,priority,status,created_at` +
    `&category=eq.${encodeURIComponent('Demande intervention')}` +
    `&status=neq.done&order=created_at.desc`;
  const r = await fetch(url, { headers: mmHeaders() });
  if (!r.ok) return [];
  return r.json();
}

/** Liste des collaborateurs MM (potentiels assignees). */
export async function fetchMMCollabs(): Promise<MMCollab[]> {
  const r = await fetch(
    `${MM_URL}/rest/v1/collaborateurs?order=id.asc`,
    { headers: mmHeaders() },
  );
  if (!r.ok) return [];
  return r.json();
}

/**
 * Crée une tâche dans MM Distribution. Si des fichiers sont fournis, ils sont
 * uploadés sur le bucket `taches-fichiers` puis la tâche est PATCHée avec la
 * liste des fichiers (champ `fichiers` jsonb sur taches).
 * Si un commentaire est fourni avec un author, il est ajouté à la table
 * `commentaires`.
 */
export async function createMMTask(input: CreateMMTaskInput): Promise<CreateMMTaskResult> {
  // 1. Créer la tâche (sans fichiers d'abord, pour avoir l'ID).
  const body = {
    title: input.title,
    category: input.category || 'Divers opérationnel',
    subtype: input.subtype || '',
    magasin: input.magasin,
    assignee_id: input.assigneeIds?.[0] ?? null,
    assignee_ids: input.assigneeIds ?? [],
    priority: input.priority,
    status: 'todo',
    due: null,
    fichiers: [],
  };
  const createRes = await fetch(`${MM_URL}/rest/v1/taches`, {
    method: 'POST',
    headers: mmHeaders({ 'Content-Type': 'application/json', Prefer: 'return=representation' }),
    body: JSON.stringify(body),
  });
  if (!createRes.ok) {
    const text = await createRes.text();
    throw new Error(`MM task create failed (${createRes.status}): ${text}`);
  }
  const task = (await createRes.json())[0] as { id: number };
  const taskId = task.id;

  // 2. Upload fichiers
  let filesUploaded = 0;
  let filesFailed = 0;
  const uploadedMeta: MMTaskFile[] = [];
  if (input.files && input.files.length > 0) {
    for (let i = 0; i < input.files.length; i++) {
      const f = input.files[i]!;
      const ext = f.name.split('.').pop() ?? 'bin';
      const path = `task-${taskId}/${Date.now()}-${i}.${ext}`;
      const upRes = await fetch(
        `${MM_URL}/storage/v1/object/taches-fichiers/${path}`,
        {
          method: 'POST',
          headers: mmHeaders({ 'Content-Type': f.type || 'application/octet-stream' }),
          body: f,
        },
      );
      if (upRes.ok) {
        uploadedMeta.push({
          name: f.name,
          path,
          type: f.type.startsWith('image') ? 'image' : 'pdf',
          url: `${MM_URL}/storage/v1/object/public/taches-fichiers/${path}`,
        });
        filesUploaded++;
      } else {
        filesFailed++;
      }
    }
    if (uploadedMeta.length > 0) {
      await fetch(`${MM_URL}/rest/v1/taches?id=eq.${taskId}`, {
        method: 'PATCH',
        headers: mmHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ fichiers: uploadedMeta }),
      });
    }
  }

  // 3. Commentaire (optionnel)
  if (input.comment?.trim() && input.commentAuthor) {
    await fetch(`${MM_URL}/rest/v1/commentaires`, {
      method: 'POST',
      headers: mmHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        tache_id: taskId,
        auteur: input.commentAuthor,
        contenu: input.comment.trim(),
      }),
    });
  }

  return { taskId, filesUploaded, filesFailed };
}

// ── Compteur nominatif « mes tâches ouvertes » ──────────────────────────
// Alimente le badge de l'entrée « To-Do » (sidebar, tuile accueil, mobile).
// On matche l'utilisateur Magic connecté à un collaborateur MM, puis on compte
// ses tâches non terminées.

// Normalise un nom pour comparaison (accents, casse, espaces).
function normName(s: string): string {
  return (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ');
}

/**
 * Résout l'id du collaborateur MM correspondant à un utilisateur Magic
 * (match par prénom, ou « prénom nom », insensible à la casse et aux accents).
 * Renvoie null si aucun collaborateur ne correspond (= pas membre de la to-do).
 */
export async function resolveMMCollabId(
  firstName: string | null,
  lastName: string | null,
): Promise<number | null> {
  const first = normName(firstName || '');
  if (!first) return null;
  const full = normName(`${firstName || ''} ${lastName || ''}`);
  const collabs = await fetchMMCollabs();
  for (const c of collabs) {
    const cn = normName(c.name || '');
    if (!cn) continue;
    const cf = cn.split(' ')[0] || cn;
    if (cn === full || cn === first || cf === first) return c.id;
  }
  return null;
}

/**
 * Nombre de tâches ouvertes (status != 'done', donc « todo » + « inprogress »)
 * assignées au collaborateur. `assignee_ids` est un jsonb (tableau d'ids) →
 * contenance jsonb `cs.[id]` ; on couvre aussi le champ scalaire `assignee_id`.
 * Le total exact est lu dans l'en-tête `content-range` (Prefer: count=exact).
 * Best-effort : projet MM séparé, renvoie 0 si indisponible.
 */
export async function fetchMMOpenTaskCount(collabId: number): Promise<number> {
  const url =
    `${MM_URL}/rest/v1/taches?select=id&status=neq.done` +
    `&or=(assignee_id.eq.${collabId},assignee_ids.cs.%5B${collabId}%5D)`;
  const r = await fetch(url, { headers: mmHeaders({ Prefer: 'count=exact', Range: '0-0' }) });
  if (!r.ok) return 0;
  const cr = r.headers.get('content-range') || '';
  const tot = cr.includes('/') ? parseInt(cr.split('/')[1] || '0', 10) : NaN;
  if (Number.isFinite(tot)) return tot;
  const rows = await r.json().catch(() => []);
  return Array.isArray(rows) ? rows.length : 0;
}

/**
 * Nombre de tâches ouvertes du collaborateur créées APRÈS `sinceIso` (= non
 * vues depuis sa dernière ouverture de la To-Do). Alimente le badge
 * « nouvelles tâches ». Best-effort : renvoie 0 si indisponible.
 */
export async function fetchMMNewTaskCount(collabId: number, sinceIso: string): Promise<number> {
  const url =
    `${MM_URL}/rest/v1/taches?select=id&status=neq.done` +
    `&or=(assignee_id.eq.${collabId},assignee_ids.cs.%5B${collabId}%5D)` +
    `&created_at=gt.${encodeURIComponent(sinceIso)}`;
  const r = await fetch(url, { headers: mmHeaders({ Prefer: 'count=exact', Range: '0-0' }) });
  if (!r.ok) return 0;
  const cr = r.headers.get('content-range') || '';
  const tot = cr.includes('/') ? parseInt(cr.split('/')[1] || '0', 10) : NaN;
  if (Number.isFinite(tot)) return tot;
  const rows = await r.json().catch(() => []);
  return Array.isArray(rows) ? rows.length : 0;
}
